import React, { useEffect, useState, useRef } from 'react';

const CircularProgress = ({
  percentage,
  size = 126,
  backgroundColor = '#EDECE8',
  animationDuration = 0.3,
  isUnlimited = false
}) => {
  // Define constants based on the design SVG
  const strokeWidth = 14;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;

  // Calculate the circumference of the circle
  const circumference = radius * 2 * Math.PI;

  // State for animation
  const [displayPercentage, setDisplayPercentage] = useState(0);
  const [circlePercentage, setCirclePercentage] = useState(0);
  const animationRef = useRef(null);
  const prevPercentageRef = useRef(percentage);

  // Determine progress color based on percentage or unlimited status
  const getProgressColor = (percent, unlimited) => {
    if (unlimited) return '#D9CFFF'; // Purple for unlimited
    if (percent < 80) return '#19A28B'; // Green for less than 80%
    if (percent < 100) return '#F9E47B'; // Yellow for 80-99%
    return '#FF825B'; // Orange for 100%
  };

  // Get the current progress color
  const progressColor = getProgressColor(circlePercentage, isUnlimited);

  // Use a normal continuous circle for both cases, not dashed
  const strokeDasharray = circumference;

  // Calculate how much of the circle to fill based on percentage
  const strokeDashoffset = isUnlimited
    ? 0 // Full circle for unlimited
    : circumference - (circlePercentage / 100) * circumference;

  // Animation effect
  useEffect(() => {
    // If unlimited, don't animate percentage
    if (isUnlimited) {
      setCirclePercentage(100); // Full circle
      setDisplayPercentage(-1); // Special value for unlimited
      return;
    }

    // Clear any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const targetPercentage = Math.min(Math.round(percentage), 100);

    // Start from current value when updating, from 0 on initial load
    const startValue = prevPercentageRef.current === percentage ? circlePercentage : displayPercentage;

    // Remember the current percentage for next update
    prevPercentageRef.current = percentage;

    // If values are the same, no need to animate
    if (Math.round(startValue) === targetPercentage) {
      return;
    }

    const startTime = performance.now();
    const duration = animationDuration * 1000; // convert to ms

    const animateValue = (timestamp) => {
      // Calculate how far through the animation we are (0 to 1)
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Use a modified easing function that doesn't slow down as much at the end
      // Mix of linear and easeOutQuad for better final digit transition
      const linearWeight = 0.7; // 70% linear, 30% eased
      const easeOutQuad = 1 - (1 - progress) * (1 - progress);
      const easeProgress = linearWeight * progress + (1 - linearWeight) * easeOutQuad;

      // Calculate the value based on the easing function
      const currentValue = startValue + easeProgress * (targetPercentage - startValue);

      // Update circle percentage for SVG
      setCirclePercentage(currentValue);

      // Update text display with rounded value
      setDisplayPercentage(Math.floor(currentValue));

      // Continue animation if not complete
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateValue);
      } else {
        // Ensure we reach the exact target at the end
        setDisplayPercentage(targetPercentage);
        setCirclePercentage(targetPercentage);
      }
    };

    // Start the animation
    animationRef.current = requestAnimationFrame(animateValue);

    // Cleanup on unmount
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [percentage, animationDuration, circlePercentage, displayPercentage, isUnlimited]);

  return (
    <div className="relative w-[160px] h-[160px] md:w-[100px] md:h-[100px] xl:w-[126px] xl:h-[126px]">
      <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
          fill="none"
        />

        {/* Progress circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>

      <div className="absolute inset-0 flex items-center justify-center">
        {isUnlimited ? (
          <span className="text-[#203B3F] text-sm md:text-base font-medium">
            <span className="text-2xl">&#8734;</span>
          </span>
        ) : (
          <span className="text-[#203B3F] text-sm md:text-base font-medium">
            {displayPercentage}%
          </span>
        )}
      </div>
    </div>
  );
};

export default CircularProgress;
